import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/PuppyApplication.css";

export const initialForm = {
  applicant: {
    fullName: "",
    preferredName: "",
    dateOfBirth: "",
    phone: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    bestTimeToContact: "",
    preferredContactMethod: "",
  },
  household: {
    members: "",
    allergiesFlag: null,
    allergiesDetails: "",
    primaryCaregiverName: "",
    primaryCaregiverRelationship: "",
  },
  housing: {
    residenceType: "",
    residenceTypeOther: "",
    ownOrRent: "",
    landlordPermissionConfirmed: false,
    yardFlag: null,
    fencedFlag: null,
    fenceDescription: "",
    dayLocation: "",
    nightLocation: "",
    hoursAlonePerDay: "",
  },
  experience: {
    ownedDogsBefore: null,
    priorDogDetails: "",
    currentlyOwnPets: null,
    currentPetsDetails: "",
    activityLevel: "",
    goal: "",
    goalOther: "",
    breedReason: "",
  },
  preferences: {
    preferredSex: "",
    preferredTemperament: "",
    colorPreference: "",
    timeframe: "",
    acknowledgesNoFirstChoice: false,
  },
  training: {
    willAttendTraining: null,
    trainingApproach: "",
    puppyChallengesPlan: "",
    exercisePlan: "",
  },
  vet: {
    hasVeterinarian: null,
    vetDetails: "",
    agreesRoutineCare: false,
    understandsGrooming: false,
  },
  commitment: {
    financiallyPrepared: null,
    rehomeReasons: "",
    agreesContactBreederFirst: false,
  },
  signature: {
    certifyTruth: false,
    understandsNoGuarantee: false,
    breederMayDecline: false,
    signatureName: "",
    signatureDate: "",
  },
};

export const STORAGE_KEY = "puppyApplicationDraft";

export const normalizeDraft = (draft) => {
  if (!draft || typeof draft !== "object") return null;
  return {
    applicant: { ...initialForm.applicant, ...(draft.applicant || {}) },
    household: { ...initialForm.household, ...(draft.household || {}) },
    housing: { ...initialForm.housing, ...(draft.housing || {}) },
    experience: { ...initialForm.experience, ...(draft.experience || {}) },
    preferences: { ...initialForm.preferences, ...(draft.preferences || {}) },
    training: { ...initialForm.training, ...(draft.training || {}) },
    vet: { ...initialForm.vet, ...(draft.vet || {}) },
    commitment: { ...initialForm.commitment, ...(draft.commitment || {}) },
    signature: { ...initialForm.signature, ...(draft.signature || {}) },
  };
};

const contactMethodLabels = {
  call: "Call",
  text: "Text",
  email: "Email",
};

const residenceTypeLabels = {
  house: "House",
  apartment: "Apartment",
  condo: "Condo",
  other: "Other",
};

const ownOrRentLabels = {
  own: "Own",
  rent: "Rent",
};

const activityLevelLabels = {
  low: "Low",
  moderate: "Moderate",
  high: "High",
};

const goalLabels = {
  companion: "Companion",
  sport: "Sport",
  therapy: "Therapy",
  breeding: "Breeding",
  other: "Other",
};

const sexLabels = {
  male: "Male",
  female: "Female",
  noPreference: "No preference",
};

const temperamentLabels = {
  laidBack: "Laid back",
  moderate: "Moderate",
  highDrive: "High drive",
  notSure: "Not sure",
};

const fieldPaths = [
  ["applicant", "fullName"],
  ["applicant", "preferredName"],
  ["applicant", "dateOfBirth"],
  ["applicant", "phone"],
  ["applicant", "email"],
  ["applicant", "addressLine1"],
  ["applicant", "addressLine2"],
  ["applicant", "city"],
  ["applicant", "state"],
  ["applicant", "zip"],
  ["applicant", "bestTimeToContact"],
  ["applicant", "preferredContactMethod"],
  ["household", "members"],
  ["household", "allergiesFlag"],
  ["household", "allergiesDetails"],
  ["household", "primaryCaregiverName"],
  ["household", "primaryCaregiverRelationship"],
  ["housing", "residenceType"],
  ["housing", "residenceTypeOther"],
  ["housing", "ownOrRent"],
  ["housing", "landlordPermissionConfirmed"],
  ["housing", "yardFlag"],
  ["housing", "fencedFlag"],
  ["housing", "fenceDescription"],
  ["housing", "dayLocation"],
  ["housing", "nightLocation"],
  ["housing", "hoursAlonePerDay"],
  ["experience", "ownedDogsBefore"],
  ["experience", "priorDogDetails"],
  ["experience", "currentlyOwnPets"],
  ["experience", "currentPetsDetails"],
  ["experience", "activityLevel"],
  ["experience", "goal"],
  ["experience", "goalOther"],
  ["experience", "breedReason"],
  ["preferences", "preferredSex"],
  ["preferences", "preferredTemperament"],
  ["preferences", "colorPreference"],
  ["preferences", "timeframe"],
  ["preferences", "acknowledgesNoFirstChoice"],
  ["training", "willAttendTraining"],
  ["training", "trainingApproach"],
  ["training", "puppyChallengesPlan"],
  ["training", "exercisePlan"],
  ["vet", "hasVeterinarian"],
  ["vet", "vetDetails"],
  ["vet", "agreesRoutineCare"],
  ["vet", "understandsGrooming"],
  ["commitment", "financiallyPrepared"],
  ["commitment", "rehomeReasons"],
  ["commitment", "agreesContactBreederFirst"],
  ["signature", "certifyTruth"],
  ["signature", "understandsNoGuarantee"],
  ["signature", "breederMayDecline"],
  ["signature", "signatureName"],
  ["signature", "signatureDate"],
];

const isEmpty = (value) => !value || !value.trim();

const normalizePhone = (value) => value.replace(/\D/g, "");

const isValidPhone = (value) => normalizePhone(value).length === 10;

const isValidEmail = (value) => /^\S+@\S+\.\S+$/.test(value);

const formatPhone = (value) => {
  const digits = normalizePhone(value);
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value || "Not provided";
};

const formatBoolean = (value) => {
  if (value === null) return "Not provided";
  return value ? "Yes" : "No";
};

const formatAgreement = (value) => (value ? "Agreed" : "Not agreed");

const formatValue = (value) => (value ? value : "Not provided");

const formatHours = (value) => {
  if (value === "" || value === null) return "Not provided";
  return `${value} hours`;
};

const mapLabel = (value, map) => (value ? map[value] || value : "Not provided");

export const buildReviewSections = (form) => [
  {
    title: "Applicant",
    items: [
      { label: "Full name", value: formatValue(form.applicant.fullName) },
      {
        label: "Preferred name",
        value: formatValue(form.applicant.preferredName),
      },
      {
        label: "Date of birth",
        value: formatValue(form.applicant.dateOfBirth),
      },
      { label: "Phone", value: formatPhone(form.applicant.phone) },
      { label: "Email", value: formatValue(form.applicant.email) },
      {
        label: "Address line 1",
        value: formatValue(form.applicant.addressLine1),
      },
      {
        label: "Address line 2",
        value: formatValue(form.applicant.addressLine2),
      },
      { label: "City", value: formatValue(form.applicant.city) },
      { label: "State", value: formatValue(form.applicant.state) },
      { label: "Zip", value: formatValue(form.applicant.zip) },
      {
        label: "Best time to contact",
        value: formatValue(form.applicant.bestTimeToContact),
      },
      {
        label: "Preferred contact method",
        value: mapLabel(form.applicant.preferredContactMethod, contactMethodLabels),
      },
    ],
  },
  {
    title: "Household",
    items: [
      { label: "Household members", value: formatValue(form.household.members) },
      {
        label: "Allergies",
        value: formatBoolean(form.household.allergiesFlag),
      },
      {
        label: "Allergy details",
        value: form.household.allergiesFlag
          ? formatValue(form.household.allergiesDetails)
          : "Not applicable",
      },
      {
        label: "Primary caregiver",
        value: formatValue(form.household.primaryCaregiverName),
      },
      {
        label: "Caregiver relationship",
        value: formatValue(form.household.primaryCaregiverRelationship),
      },
    ],
  },
  {
    title: "Housing",
    items: [
      {
        label: "Residence type",
        value: mapLabel(form.housing.residenceType, residenceTypeLabels),
      },
      {
        label: "Residence details",
        value:
          form.housing.residenceType === "other"
            ? formatValue(form.housing.residenceTypeOther)
            : "Not applicable",
      },
      {
        label: "Own or rent",
        value: mapLabel(form.housing.ownOrRent, ownOrRentLabels),
      },
      {
        label: "Landlord permission",
        value:
          form.housing.ownOrRent === "rent"
            ? formatAgreement(form.housing.landlordPermissionConfirmed)
            : "Not applicable",
      },
      { label: "Yard", value: formatBoolean(form.housing.yardFlag) },
      {
        label: "Fenced yard",
        value: form.housing.yardFlag
          ? formatBoolean(form.housing.fencedFlag)
          : "Not applicable",
      },
      {
        label: "Fence description",
        value:
          form.housing.yardFlag && form.housing.fencedFlag
            ? formatValue(form.housing.fenceDescription)
            : "Not applicable",
      },
      {
        label: "Day location",
        value: formatValue(form.housing.dayLocation),
      },
      {
        label: "Night location",
        value: formatValue(form.housing.nightLocation),
      },
      {
        label: "Hours alone per day",
        value: formatHours(form.housing.hoursAlonePerDay),
      },
    ],
  },
  {
    title: "Experience",
    items: [
      {
        label: "Owned dogs before",
        value: formatBoolean(form.experience.ownedDogsBefore),
      },
      {
        label: "Prior dog details",
        value: form.experience.ownedDogsBefore
          ? formatValue(form.experience.priorDogDetails)
          : "Not applicable",
      },
      {
        label: "Currently own pets",
        value: formatBoolean(form.experience.currentlyOwnPets),
      },
      {
        label: "Current pets details",
        value: form.experience.currentlyOwnPets
          ? formatValue(form.experience.currentPetsDetails)
          : "Not applicable",
      },
      {
        label: "Activity level",
        value: mapLabel(form.experience.activityLevel, activityLevelLabels),
      },
      {
        label: "Goal",
        value: mapLabel(form.experience.goal, goalLabels),
      },
      {
        label: "Goal details",
        value:
          form.experience.goal === "other"
            ? formatValue(form.experience.goalOther)
            : "Not applicable",
      },
      {
        label: "Breed reason",
        value: formatValue(form.experience.breedReason),
      },
    ],
  },
  {
    title: "Preferences",
    items: [
      {
        label: "Preferred sex",
        value: mapLabel(form.preferences.preferredSex, sexLabels),
      },
      {
        label: "Preferred temperament",
        value: mapLabel(form.preferences.preferredTemperament, temperamentLabels),
      },
      {
        label: "Color preference",
        value: formatValue(form.preferences.colorPreference),
      },
      {
        label: "Timeframe",
        value: formatValue(form.preferences.timeframe),
      },
      {
        label: "No first choice acknowledgment",
        value: formatAgreement(form.preferences.acknowledgesNoFirstChoice),
      },
    ],
  },
  {
    title: "Training",
    items: [
      {
        label: "Will attend training",
        value: formatBoolean(form.training.willAttendTraining),
      },
      {
        label: "Training approach",
        value: formatValue(form.training.trainingApproach),
      },
      {
        label: "Puppy challenges plan",
        value: formatValue(form.training.puppyChallengesPlan),
      },
      {
        label: "Exercise plan",
        value: formatValue(form.training.exercisePlan),
      },
    ],
  },
  {
    title: "Veterinarian",
    items: [
      {
        label: "Has veterinarian",
        value: formatBoolean(form.vet.hasVeterinarian),
      },
      {
        label: "Veterinarian details",
        value: form.vet.hasVeterinarian
          ? formatValue(form.vet.vetDetails)
          : "Not applicable",
      },
      {
        label: "Routine care agreement",
        value: formatAgreement(form.vet.agreesRoutineCare),
      },
      {
        label: "Grooming acknowledgment",
        value: formatAgreement(form.vet.understandsGrooming),
      },
    ],
  },
  {
    title: "Commitment",
    items: [
      {
        label: "Financially prepared",
        value: formatBoolean(form.commitment.financiallyPrepared),
      },
      {
        label: "Rehome plan",
        value: formatValue(form.commitment.rehomeReasons),
      },
      {
        label: "Contact breeder first",
        value: formatAgreement(form.commitment.agreesContactBreederFirst),
      },
    ],
  },
  {
    title: "Signature",
    items: [
      {
        label: "Certify truth",
        value: formatAgreement(form.signature.certifyTruth),
      },
      {
        label: "No guarantee acknowledgment",
        value: formatAgreement(form.signature.understandsNoGuarantee),
      },
      {
        label: "Breeder may decline",
        value: formatAgreement(form.signature.breederMayDecline),
      },
      {
        label: "Signature name",
        value: formatValue(form.signature.signatureName),
      },
      {
        label: "Signature date",
        value: formatValue(form.signature.signatureDate),
      },
    ],
  },
];

const DEFAULT_PDF_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "https://declercksgoldens.com/api/puppy-application"
    : "https://declercksgoldens.com/api/puppy-application";

const PDF_ENDPOINT =
  process.env.REACT_APP_PUPPY_PDF_ENDPOINT || DEFAULT_PDF_ENDPOINT;

export const submitApplication = async (payload) => {
  const response = await fetch(PDF_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/pdf",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = "Submission failed. Please try again.";
    try {
      const data = await response.json();
      if (data?.error) {
        message = data.error;
      }
    } catch (error) {
      try {
        const text = await response.text();
        if (text) {
          message = text;
        }
      } catch (textError) {
        // ignore read errors
      }
    }
    throw new Error(message);
  }

  const pdfBlob = await response.blob();
  return { pdfBlob, filename: "puppy-application.pdf" };
};

const validateField = (form, section, field) => {
  const value = form[section][field];
  switch (`${section}.${field}`) {
    case "applicant.fullName":
      return isEmpty(value) ? "Full name is required." : "";
    case "applicant.phone":
      if (isEmpty(value)) return "Phone is required.";
      return isValidPhone(value) ? "" : "Enter a 10-digit phone number.";
    case "applicant.email":
      if (isEmpty(value)) return "Email is required.";
      return isValidEmail(value) ? "" : "Enter a valid email address.";
    case "applicant.addressLine1":
      return isEmpty(value) ? "Address line 1 is required." : "";
    case "applicant.city":
      return isEmpty(value) ? "City is required." : "";
    case "applicant.state":
      if (isEmpty(value)) return "State is required.";
      return /^[A-Za-z]{2}$/.test(value.trim())
        ? ""
        : "State must be 2 letters.";
    case "applicant.zip":
      return isEmpty(value) ? "Zip is required." : "";
    case "applicant.preferredContactMethod":
      return value ? "" : "Preferred contact method is required.";
    case "household.members":
      return isEmpty(value) ? "Household members are required." : "";
    case "household.allergiesFlag":
      return value === null
        ? "Please indicate if anyone has allergies."
        : "";
    case "household.allergiesDetails":
      return form.household.allergiesFlag && isEmpty(value)
        ? "Please describe the allergies."
        : "";
    case "household.primaryCaregiverName":
      return isEmpty(value) ? "Primary caregiver name is required." : "";
    case "housing.residenceType":
      return value ? "" : "Residence type is required.";
    case "housing.residenceTypeOther":
      return form.housing.residenceType === "other" && isEmpty(value)
        ? "Please describe the residence type."
        : "";
    case "housing.ownOrRent":
      return value ? "" : "Please indicate if you own or rent.";
    case "housing.landlordPermissionConfirmed":
      return form.housing.ownOrRent === "rent" && !value
        ? "Please confirm landlord permission."
        : "";
    case "housing.yardFlag":
      return value === null ? "Please indicate if you have a yard." : "";
    case "housing.fencedFlag":
      return form.housing.yardFlag && value === null
        ? "Please indicate if the yard is fenced."
        : "";
    case "housing.dayLocation":
      return isEmpty(value)
        ? "Please describe where the puppy stays during the day."
        : "";
    case "housing.nightLocation":
      return isEmpty(value)
        ? "Please describe where the puppy sleeps at night."
        : "";
    case "housing.hoursAlonePerDay":
      if (value === "") return "Hours alone per day is required.";
      if (Number.isNaN(Number(value))) {
        return "Hours alone must be between 0 and 24.";
      }
      return Number(value) >= 0 && Number(value) <= 24
        ? ""
        : "Hours alone must be between 0 and 24.";
    case "experience.ownedDogsBefore":
      return value === null
        ? "Please indicate if you have owned dogs before."
        : "";
    case "experience.priorDogDetails":
      return form.experience.ownedDogsBefore && isEmpty(value)
        ? "Please share your prior dog experience."
        : "";
    case "experience.currentlyOwnPets":
      return value === null
        ? "Please indicate if you currently own pets."
        : "";
    case "experience.currentPetsDetails":
      return form.experience.currentlyOwnPets && isEmpty(value)
        ? "Please list your current pets."
        : "";
    case "experience.activityLevel":
      return value ? "" : "Activity level is required.";
    case "experience.goal":
      return value ? "" : "Please select your primary goal.";
    case "experience.goalOther":
      return form.experience.goal === "other" && isEmpty(value)
        ? "Please describe your goal."
        : "";
    case "experience.breedReason":
      return isEmpty(value) ? "Please tell us why this breed is a fit." : "";
    case "preferences.preferredSex":
      return value ? "" : "Preferred sex is required.";
    case "preferences.preferredTemperament":
      return value ? "" : "Preferred temperament is required.";
    case "preferences.acknowledgesNoFirstChoice":
      return value
        ? ""
        : "Please acknowledge that first choice is not guaranteed.";
    case "training.willAttendTraining":
      return value === null
        ? "Please indicate if you will attend training."
        : "";
    case "training.puppyChallengesPlan":
      return isEmpty(value)
        ? "Please share your plan for puppy challenges."
        : "";
    case "training.exercisePlan":
      return isEmpty(value) ? "Please describe your exercise plan." : "";
    case "vet.hasVeterinarian":
      return value === null
        ? "Please indicate if you have a veterinarian."
        : "";
    case "vet.vetDetails":
      return form.vet.hasVeterinarian && isEmpty(value)
        ? "Please share your veterinarian details."
        : "";
    case "vet.agreesRoutineCare":
      return value ? "" : "Please agree to routine veterinary care.";
    case "vet.understandsGrooming":
      return value ? "" : "Please acknowledge grooming needs.";
    case "commitment.financiallyPrepared":
      return value === null
        ? "Please indicate if you are financially prepared."
        : "";
    case "commitment.rehomeReasons":
      return isEmpty(value) ? "Please share your rehoming plan." : "";
    case "commitment.agreesContactBreederFirst":
      return value ? "" : "Please agree to contact the breeder first.";
    case "signature.certifyTruth":
      return value ? "" : "Please certify the information is truthful.";
    case "signature.understandsNoGuarantee":
      return value ? "" : "Please acknowledge there is no guarantee.";
    case "signature.breederMayDecline":
      return value ? "" : "Please acknowledge the breeder may decline.";
    case "signature.signatureName":
      return isEmpty(value) ? "Signature name is required." : "";
    case "signature.signatureDate":
      return value ? "" : "Signature date is required.";
    default:
      return "";
  }
};

export const validateAll = (form) =>
  fieldPaths.reduce((acc, [section, field]) => {
    const error = validateField(form, section, field);
    if (error) {
      acc[`${section}.${field}`] = error;
    }
    return acc;
  }, {});

export default function PuppyApplicationPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [hasRestored, setHasRestored] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const normalized = normalizeDraft(parsed);
        if (normalized) {
          setForm(normalized);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (removeError) {
        // ignore storage errors
      }
    } finally {
      setHasRestored(true);
    }
  }, []);

  useEffect(() => {
    if (!hasRestored) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch (error) {
      // ignore storage errors
    }
  }, [form, hasRestored]);

  const handleBlur = (section, field) => {
    const path = `${section}.${field}`;
    setTouched((prev) => ({ ...prev, [path]: true }));
    const error = validateField(form, section, field);
    setErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next[path] = error;
      } else {
        delete next[path];
      }
      return next;
    });
  };

  const handleChange = (section, field, rawValue) => {
    setForm((prev) => {
      let value = rawValue;
      if (section === "applicant" && field === "state") {
        value = rawValue.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2);
      }
      const next = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };

      if (section === "household" && field === "allergiesFlag" && value === false) {
        next.household.allergiesDetails = "";
      }
      if (section === "housing" && field === "residenceType" && value !== "other") {
        next.housing.residenceTypeOther = "";
      }
      if (section === "housing" && field === "ownOrRent" && value !== "rent") {
        next.housing.landlordPermissionConfirmed = false;
      }
      if (section === "housing" && field === "yardFlag" && value === false) {
        next.housing.fencedFlag = null;
        next.housing.fenceDescription = "";
      }
      if (section === "housing" && field === "fencedFlag" && value === false) {
        next.housing.fenceDescription = "";
      }
      if (section === "experience" && field === "ownedDogsBefore" && value === false) {
        next.experience.priorDogDetails = "";
      }
      if (
        section === "experience" &&
        field === "currentlyOwnPets" &&
        value === false
      ) {
        next.experience.currentPetsDetails = "";
      }
      if (section === "experience" && field === "goal" && value !== "other") {
        next.experience.goalOther = "";
      }
      if (section === "vet" && field === "hasVeterinarian" && value === false) {
        next.vet.vetDetails = "";
      }

      const path = `${section}.${field}`;
      if (submitAttempted || touched[path]) {
        const error = validateField(next, section, field);
        setErrors((prevErrors) => {
          const updated = { ...prevErrors };
          if (error) {
            updated[path] = error;
          } else {
            delete updated[path];
          }
          return updated;
        });
      }

      return next;
    });
  };

  const handleReview = () => {
    const nextErrors = validateAll(form);
    setErrors(nextErrors);
    setSubmitAttempted(true);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch (error) {
      // ignore storage errors
    }
    navigate("/puppy-application/review");
  };

  const handleReviewSubmit = (event) => {
    event.preventDefault();
    handleReview();
  };

  const handleReset = () => {
    if (!window.confirm("Reset the application and clear all fields?")) {
      return;
    }
    setForm(initialForm);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // ignore storage errors
    }
  };

  const hasErrors = submitAttempted && Object.keys(errors).length > 0;

  return (
    <div className="puppy-app-page">
      <div className="puppy-app-header fade-up">
        <div>
          <p className="puppy-app-kicker">Puppy Purchase Application</p>
          <h1 className="puppy-app-title">Puppy Purchase Application</h1>
        </div>
        <p className="puppy-app-intro">
          This application helps us ensure each puppy is placed in a safe and
          appropriate home. Please complete the form fully. Submitting an
          application does not guarantee approval or placement.
        </p>
      </div>

      <form
        className="puppy-app-form fade-up"
        onSubmit={handleReviewSubmit}
        noValidate
      >
        {hasErrors && (
          <div className="warning-banner" role="status">
            Please fix the highlighted fields before reviewing.
          </div>
        )}

        <section className="puppy-section">
          <div className="section-header">
            <h2>Applicant</h2>
            <p>Primary applicant information.</p>
          </div>
          <div className="field-grid">
            <div className="form-field">
              <label htmlFor="applicant-fullName" className="field-label">
                Full name <span className="required">*</span>
              </label>
              <input
                id="applicant-fullName"
                type="text"
                value={form.applicant.fullName}
                autoComplete="name"
                onChange={(e) =>
                  handleChange("applicant", "fullName", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "fullName")}
                aria-invalid={Boolean(errors["applicant.fullName"])}
                aria-describedby={
                  errors["applicant.fullName"]
                    ? "applicant-fullName-error"
                    : undefined
                }
              />
              {errors["applicant.fullName"] && (
                <p className="field-error" id="applicant-fullName-error">
                  {errors["applicant.fullName"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="applicant-preferredName" className="field-label">
                Preferred name
              </label>
              <input
                id="applicant-preferredName"
                type="text"
                value={form.applicant.preferredName}
                onChange={(e) =>
                  handleChange("applicant", "preferredName", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "preferredName")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="applicant-dateOfBirth" className="field-label">
                Date of birth
              </label>
              <input
                id="applicant-dateOfBirth"
                type="date"
                value={form.applicant.dateOfBirth}
                onChange={(e) =>
                  handleChange("applicant", "dateOfBirth", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "dateOfBirth")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="applicant-phone" className="field-label">
                Phone <span className="required">*</span>
              </label>
              <input
                id="applicant-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={form.applicant.phone}
                onChange={(e) =>
                  handleChange("applicant", "phone", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "phone")}
                aria-invalid={Boolean(errors["applicant.phone"])}
                aria-describedby={
                  errors["applicant.phone"] ? "applicant-phone-error" : undefined
                }
              />
              {errors["applicant.phone"] && (
                <p className="field-error" id="applicant-phone-error">
                  {errors["applicant.phone"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="applicant-email" className="field-label">
                Email <span className="required">*</span>
              </label>
              <input
                id="applicant-email"
                type="email"
                autoComplete="email"
                value={form.applicant.email}
                onChange={(e) =>
                  handleChange("applicant", "email", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "email")}
                aria-invalid={Boolean(errors["applicant.email"])}
                aria-describedby={
                  errors["applicant.email"] ? "applicant-email-error" : undefined
                }
              />
              {errors["applicant.email"] && (
                <p className="field-error" id="applicant-email-error">
                  {errors["applicant.email"]}
                </p>
              )}
            </div>

            <div className="form-field field-span">
              <label htmlFor="applicant-addressLine1" className="field-label">
                Address line 1 <span className="required">*</span>
              </label>
              <input
                id="applicant-addressLine1"
                type="text"
                autoComplete="address-line1"
                value={form.applicant.addressLine1}
                onChange={(e) =>
                  handleChange("applicant", "addressLine1", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "addressLine1")}
                aria-invalid={Boolean(errors["applicant.addressLine1"])}
                aria-describedby={
                  errors["applicant.addressLine1"]
                    ? "applicant-addressLine1-error"
                    : undefined
                }
              />
              {errors["applicant.addressLine1"] && (
                <p className="field-error" id="applicant-addressLine1-error">
                  {errors["applicant.addressLine1"]}
                </p>
              )}
            </div>

            <div className="form-field field-span">
              <label htmlFor="applicant-addressLine2" className="field-label">
                Address line 2
              </label>
              <input
                id="applicant-addressLine2"
                type="text"
                autoComplete="address-line2"
                value={form.applicant.addressLine2}
                onChange={(e) =>
                  handleChange("applicant", "addressLine2", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "addressLine2")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="applicant-city" className="field-label">
                City <span className="required">*</span>
              </label>
              <input
                id="applicant-city"
                type="text"
                autoComplete="address-level2"
                value={form.applicant.city}
                onChange={(e) =>
                  handleChange("applicant", "city", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "city")}
                aria-invalid={Boolean(errors["applicant.city"])}
                aria-describedby={
                  errors["applicant.city"] ? "applicant-city-error" : undefined
                }
              />
              {errors["applicant.city"] && (
                <p className="field-error" id="applicant-city-error">
                  {errors["applicant.city"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="applicant-state" className="field-label">
                State <span className="required">*</span>
              </label>
              <input
                id="applicant-state"
                type="text"
                autoComplete="address-level1"
                value={form.applicant.state}
                onChange={(e) =>
                  handleChange("applicant", "state", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "state")}
                maxLength={2}
                aria-invalid={Boolean(errors["applicant.state"])}
                aria-describedby={
                  errors["applicant.state"] ? "applicant-state-error" : undefined
                }
              />
              {errors["applicant.state"] && (
                <p className="field-error" id="applicant-state-error">
                  {errors["applicant.state"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="applicant-zip" className="field-label">
                Zip <span className="required">*</span>
              </label>
              <input
                id="applicant-zip"
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                value={form.applicant.zip}
                onChange={(e) =>
                  handleChange("applicant", "zip", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "zip")}
                aria-invalid={Boolean(errors["applicant.zip"])}
                aria-describedby={
                  errors["applicant.zip"] ? "applicant-zip-error" : undefined
                }
              />
              {errors["applicant.zip"] && (
                <p className="field-error" id="applicant-zip-error">
                  {errors["applicant.zip"]}
                </p>
              )}
            </div>

            <div className="form-field field-span">
              <label htmlFor="applicant-bestTimeToContact" className="field-label">
                Best time to contact
              </label>
              <input
                id="applicant-bestTimeToContact"
                type="text"
                value={form.applicant.bestTimeToContact}
                onChange={(e) =>
                  handleChange("applicant", "bestTimeToContact", e.target.value)
                }
                onBlur={() => handleBlur("applicant", "bestTimeToContact")}
              />
            </div>

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Preferred contact method <span className="required">*</span>
              </legend>
              <div className="option-group">
                {Object.entries(contactMethodLabels).map(([value, label]) => (
                  <label key={value} className="option-pill">
                    <input
                      type="radio"
                      name="applicant-preferredContactMethod"
                      checked={form.applicant.preferredContactMethod === value}
                      onChange={() =>
                        handleChange("applicant", "preferredContactMethod", value)
                      }
                      onBlur={() =>
                        handleBlur("applicant", "preferredContactMethod")
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
              {errors["applicant.preferredContactMethod"] && (
                <p className="field-error">
                  {errors["applicant.preferredContactMethod"]}
                </p>
              )}
            </fieldset>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Household</h2>
            <p>Who lives in the home and supports the puppy.</p>
          </div>
          <div className="field-grid">
            <div className="form-field field-span">
              <label htmlFor="household-members" className="field-label">
                Household members (include ages of children){" "}
                <span className="required">*</span>
              </label>
              <textarea
                id="household-members"
                rows={3}
                value={form.household.members}
                onChange={(e) =>
                  handleChange("household", "members", e.target.value)
                }
                onBlur={() => handleBlur("household", "members")}
                aria-invalid={Boolean(errors["household.members"])}
                aria-describedby={
                  errors["household.members"] ? "household-members-error" : undefined
                }
              />
              {errors["household.members"] && (
                <p className="field-error" id="household-members-error">
                  {errors["household.members"]}
                </p>
              )}
            </div>

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Any allergies in the household? <span className="required">*</span>
              </legend>
              <div className="option-group">
                <label className="option-pill">
                  <input
                    type="radio"
                    name="household-allergiesFlag"
                    checked={form.household.allergiesFlag === true}
                    onChange={() =>
                      handleChange("household", "allergiesFlag", true)
                    }
                    onBlur={() => handleBlur("household", "allergiesFlag")}
                  />
                  Yes
                </label>
                <label className="option-pill">
                  <input
                    type="radio"
                    name="household-allergiesFlag"
                    checked={form.household.allergiesFlag === false}
                    onChange={() =>
                      handleChange("household", "allergiesFlag", false)
                    }
                    onBlur={() => handleBlur("household", "allergiesFlag")}
                  />
                  No
                </label>
              </div>
              {errors["household.allergiesFlag"] && (
                <p className="field-error">{errors["household.allergiesFlag"]}</p>
              )}
            </fieldset>

            {form.household.allergiesFlag && (
              <div className="form-field field-span">
                <label htmlFor="household-allergiesDetails" className="field-label">
                  Allergy details <span className="required">*</span>
                </label>
                <textarea
                  id="household-allergiesDetails"
                  rows={3}
                  value={form.household.allergiesDetails}
                  onChange={(e) =>
                    handleChange("household", "allergiesDetails", e.target.value)
                  }
                  onBlur={() => handleBlur("household", "allergiesDetails")}
                  aria-invalid={Boolean(errors["household.allergiesDetails"])}
                  aria-describedby={
                    errors["household.allergiesDetails"]
                      ? "household-allergiesDetails-error"
                      : undefined
                  }
                />
                {errors["household.allergiesDetails"] && (
                  <p
                    className="field-error"
                    id="household-allergiesDetails-error"
                  >
                    {errors["household.allergiesDetails"]}
                  </p>
                )}
              </div>
            )}

            <div className="form-field">
              <label htmlFor="household-primaryCaregiverName" className="field-label">
                Primary caregiver name <span className="required">*</span>
              </label>
              <input
                id="household-primaryCaregiverName"
                type="text"
                value={form.household.primaryCaregiverName}
                onChange={(e) =>
                  handleChange("household", "primaryCaregiverName", e.target.value)
                }
                onBlur={() => handleBlur("household", "primaryCaregiverName")}
                aria-invalid={Boolean(errors["household.primaryCaregiverName"])}
                aria-describedby={
                  errors["household.primaryCaregiverName"]
                    ? "household-primaryCaregiverName-error"
                    : undefined
                }
              />
              {errors["household.primaryCaregiverName"] && (
                <p
                  className="field-error"
                  id="household-primaryCaregiverName-error"
                >
                  {errors["household.primaryCaregiverName"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label
                htmlFor="household-primaryCaregiverRelationship"
                className="field-label"
              >
                Caregiver relationship
              </label>
              <input
                id="household-primaryCaregiverRelationship"
                type="text"
                value={form.household.primaryCaregiverRelationship}
                onChange={(e) =>
                  handleChange(
                    "household",
                    "primaryCaregiverRelationship",
                    e.target.value
                  )
                }
                onBlur={() =>
                  handleBlur("household", "primaryCaregiverRelationship")
                }
              />
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Housing</h2>
            <p>Your home environment and daily routine.</p>
          </div>
          <div className="field-grid">
            <fieldset className="form-field field-span">
              <legend className="field-label">
                Residence type <span className="required">*</span>
              </legend>
              <div className="option-group">
                {Object.entries(residenceTypeLabels).map(([value, label]) => (
                  <label key={value} className="option-pill">
                    <input
                      type="radio"
                      name="housing-residenceType"
                      checked={form.housing.residenceType === value}
                      onChange={() =>
                        handleChange("housing", "residenceType", value)
                      }
                      onBlur={() => handleBlur("housing", "residenceType")}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {errors["housing.residenceType"] && (
                <p className="field-error">{errors["housing.residenceType"]}</p>
              )}
            </fieldset>

            {form.housing.residenceType === "other" && (
              <div className="form-field field-span">
                <label htmlFor="housing-residenceTypeOther" className="field-label">
                  Residence type details <span className="required">*</span>
                </label>
                <input
                  id="housing-residenceTypeOther"
                  type="text"
                  value={form.housing.residenceTypeOther}
                  onChange={(e) =>
                    handleChange("housing", "residenceTypeOther", e.target.value)
                  }
                  onBlur={() => handleBlur("housing", "residenceTypeOther")}
                  aria-invalid={Boolean(errors["housing.residenceTypeOther"])}
                  aria-describedby={
                    errors["housing.residenceTypeOther"]
                      ? "housing-residenceTypeOther-error"
                      : undefined
                  }
                />
                {errors["housing.residenceTypeOther"] && (
                  <p
                    className="field-error"
                    id="housing-residenceTypeOther-error"
                  >
                    {errors["housing.residenceTypeOther"]}
                  </p>
                )}
              </div>
            )}

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Do you own or rent? <span className="required">*</span>
              </legend>
              <div className="option-group">
                {Object.entries(ownOrRentLabels).map(([value, label]) => (
                  <label key={value} className="option-pill">
                    <input
                      type="radio"
                      name="housing-ownOrRent"
                      checked={form.housing.ownOrRent === value}
                      onChange={() =>
                        handleChange("housing", "ownOrRent", value)
                      }
                      onBlur={() => handleBlur("housing", "ownOrRent")}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {errors["housing.ownOrRent"] && (
                <p className="field-error">{errors["housing.ownOrRent"]}</p>
              )}
            </fieldset>

            {form.housing.ownOrRent === "rent" && (
              <div className="form-field field-span">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.housing.landlordPermissionConfirmed}
                    onChange={(e) =>
                      handleChange(
                        "housing",
                        "landlordPermissionConfirmed",
                        e.target.checked
                      )
                    }
                    onBlur={() =>
                      handleBlur("housing", "landlordPermissionConfirmed")
                    }
                  />
                  Landlord permission is confirmed{" "}
                  <span className="required">*</span>
                </label>
                {errors["housing.landlordPermissionConfirmed"] && (
                  <p className="field-error">
                    {errors["housing.landlordPermissionConfirmed"]}
                  </p>
                )}
              </div>
            )}

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Do you have a yard? <span className="required">*</span>
              </legend>
              <div className="option-group">
                <label className="option-pill">
                  <input
                    type="radio"
                    name="housing-yardFlag"
                    checked={form.housing.yardFlag === true}
                    onChange={() => handleChange("housing", "yardFlag", true)}
                    onBlur={() => handleBlur("housing", "yardFlag")}
                  />
                  Yes
                </label>
                <label className="option-pill">
                  <input
                    type="radio"
                    name="housing-yardFlag"
                    checked={form.housing.yardFlag === false}
                    onChange={() => handleChange("housing", "yardFlag", false)}
                    onBlur={() => handleBlur("housing", "yardFlag")}
                  />
                  No
                </label>
              </div>
              {errors["housing.yardFlag"] && (
                <p className="field-error">{errors["housing.yardFlag"]}</p>
              )}
            </fieldset>

            {form.housing.yardFlag && (
              <fieldset className="form-field field-span">
                <legend className="field-label">
                  Is the yard fenced? <span className="required">*</span>
                </legend>
                <div className="option-group">
                  <label className="option-pill">
                    <input
                      type="radio"
                      name="housing-fencedFlag"
                      checked={form.housing.fencedFlag === true}
                      onChange={() =>
                        handleChange("housing", "fencedFlag", true)
                      }
                      onBlur={() => handleBlur("housing", "fencedFlag")}
                    />
                    Yes
                  </label>
                  <label className="option-pill">
                    <input
                      type="radio"
                      name="housing-fencedFlag"
                      checked={form.housing.fencedFlag === false}
                      onChange={() =>
                        handleChange("housing", "fencedFlag", false)
                      }
                      onBlur={() => handleBlur("housing", "fencedFlag")}
                    />
                    No
                  </label>
                </div>
                {errors["housing.fencedFlag"] && (
                  <p className="field-error">{errors["housing.fencedFlag"]}</p>
                )}
              </fieldset>
            )}

            {form.housing.yardFlag && form.housing.fencedFlag === true && (
              <div className="form-field field-span">
                <label htmlFor="housing-fenceDescription" className="field-label">
                  Fence description
                </label>
                <textarea
                  id="housing-fenceDescription"
                  rows={3}
                  value={form.housing.fenceDescription}
                  onChange={(e) =>
                    handleChange("housing", "fenceDescription", e.target.value)
                  }
                  onBlur={() => handleBlur("housing", "fenceDescription")}
                />
              </div>
            )}

            <div className="form-field field-span">
              <label htmlFor="housing-dayLocation" className="field-label">
                Where will the puppy stay during the day?{" "}
                <span className="required">*</span>
              </label>
              <textarea
                id="housing-dayLocation"
                rows={3}
                value={form.housing.dayLocation}
                onChange={(e) =>
                  handleChange("housing", "dayLocation", e.target.value)
                }
                onBlur={() => handleBlur("housing", "dayLocation")}
                aria-invalid={Boolean(errors["housing.dayLocation"])}
                aria-describedby={
                  errors["housing.dayLocation"] ? "housing-dayLocation-error" : undefined
                }
              />
              {errors["housing.dayLocation"] && (
                <p className="field-error" id="housing-dayLocation-error">
                  {errors["housing.dayLocation"]}
                </p>
              )}
            </div>

            <div className="form-field field-span">
              <label htmlFor="housing-nightLocation" className="field-label">
                Where will the puppy sleep at night?{" "}
                <span className="required">*</span>
              </label>
              <textarea
                id="housing-nightLocation"
                rows={3}
                value={form.housing.nightLocation}
                onChange={(e) =>
                  handleChange("housing", "nightLocation", e.target.value)
                }
                onBlur={() => handleBlur("housing", "nightLocation")}
                aria-invalid={Boolean(errors["housing.nightLocation"])}
                aria-describedby={
                  errors["housing.nightLocation"]
                    ? "housing-nightLocation-error"
                    : undefined
                }
              />
              {errors["housing.nightLocation"] && (
                <p className="field-error" id="housing-nightLocation-error">
                  {errors["housing.nightLocation"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="housing-hoursAlonePerDay" className="field-label">
                Hours alone per day <span className="required">*</span>
              </label>
              <input
                id="housing-hoursAlonePerDay"
                type="number"
                min="0"
                max="24"
                value={form.housing.hoursAlonePerDay}
                onChange={(e) =>
                  handleChange("housing", "hoursAlonePerDay", e.target.value)
                }
                onBlur={() => handleBlur("housing", "hoursAlonePerDay")}
                aria-invalid={Boolean(errors["housing.hoursAlonePerDay"])}
                aria-describedby={
                  errors["housing.hoursAlonePerDay"]
                    ? "housing-hoursAlonePerDay-error"
                    : undefined
                }
              />
              {errors["housing.hoursAlonePerDay"] && (
                <p className="field-error" id="housing-hoursAlonePerDay-error">
                  {errors["housing.hoursAlonePerDay"]}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Experience</h2>
            <p>Your experience with dogs and goals for this puppy.</p>
          </div>
          <div className="field-grid">
            <fieldset className="form-field field-span">
              <legend className="field-label">
                Have you owned dogs before? <span className="required">*</span>
              </legend>
              <div className="option-group">
                <label className="option-pill">
                  <input
                    type="radio"
                    name="experience-ownedDogsBefore"
                    checked={form.experience.ownedDogsBefore === true}
                    onChange={() =>
                      handleChange("experience", "ownedDogsBefore", true)
                    }
                    onBlur={() => handleBlur("experience", "ownedDogsBefore")}
                  />
                  Yes
                </label>
                <label className="option-pill">
                  <input
                    type="radio"
                    name="experience-ownedDogsBefore"
                    checked={form.experience.ownedDogsBefore === false}
                    onChange={() =>
                      handleChange("experience", "ownedDogsBefore", false)
                    }
                    onBlur={() => handleBlur("experience", "ownedDogsBefore")}
                  />
                  No
                </label>
              </div>
              {errors["experience.ownedDogsBefore"] && (
                <p className="field-error">{errors["experience.ownedDogsBefore"]}</p>
              )}
            </fieldset>

            {form.experience.ownedDogsBefore && (
              <div className="form-field field-span">
                <label htmlFor="experience-priorDogDetails" className="field-label">
                  Prior dog details <span className="required">*</span>
                </label>
                <textarea
                  id="experience-priorDogDetails"
                  rows={3}
                  value={form.experience.priorDogDetails}
                  onChange={(e) =>
                    handleChange("experience", "priorDogDetails", e.target.value)
                  }
                  onBlur={() => handleBlur("experience", "priorDogDetails")}
                  aria-invalid={Boolean(errors["experience.priorDogDetails"])}
                  aria-describedby={
                    errors["experience.priorDogDetails"]
                      ? "experience-priorDogDetails-error"
                      : undefined
                  }
                />
                {errors["experience.priorDogDetails"] && (
                  <p
                    className="field-error"
                    id="experience-priorDogDetails-error"
                  >
                    {errors["experience.priorDogDetails"]}
                  </p>
                )}
              </div>
            )}

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Do you currently own pets? <span className="required">*</span>
              </legend>
              <div className="option-group">
                <label className="option-pill">
                  <input
                    type="radio"
                    name="experience-currentlyOwnPets"
                    checked={form.experience.currentlyOwnPets === true}
                    onChange={() =>
                      handleChange("experience", "currentlyOwnPets", true)
                    }
                    onBlur={() => handleBlur("experience", "currentlyOwnPets")}
                  />
                  Yes
                </label>
                <label className="option-pill">
                  <input
                    type="radio"
                    name="experience-currentlyOwnPets"
                    checked={form.experience.currentlyOwnPets === false}
                    onChange={() =>
                      handleChange("experience", "currentlyOwnPets", false)
                    }
                    onBlur={() => handleBlur("experience", "currentlyOwnPets")}
                  />
                  No
                </label>
              </div>
              {errors["experience.currentlyOwnPets"] && (
                <p className="field-error">
                  {errors["experience.currentlyOwnPets"]}
                </p>
              )}
            </fieldset>

            {form.experience.currentlyOwnPets && (
              <div className="form-field field-span">
                <label htmlFor="experience-currentPetsDetails" className="field-label">
                  Current pets details <span className="required">*</span>
                </label>
                <textarea
                  id="experience-currentPetsDetails"
                  rows={3}
                  value={form.experience.currentPetsDetails}
                  onChange={(e) =>
                    handleChange(
                      "experience",
                      "currentPetsDetails",
                      e.target.value
                    )
                  }
                  onBlur={() => handleBlur("experience", "currentPetsDetails")}
                  aria-invalid={Boolean(errors["experience.currentPetsDetails"])}
                  aria-describedby={
                    errors["experience.currentPetsDetails"]
                      ? "experience-currentPetsDetails-error"
                      : undefined
                  }
                />
                {errors["experience.currentPetsDetails"] && (
                  <p
                    className="field-error"
                    id="experience-currentPetsDetails-error"
                  >
                    {errors["experience.currentPetsDetails"]}
                  </p>
                )}
              </div>
            )}

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Activity level <span className="required">*</span>
              </legend>
              <div className="option-group">
                {Object.entries(activityLevelLabels).map(([value, label]) => (
                  <label key={value} className="option-pill">
                    <input
                      type="radio"
                      name="experience-activityLevel"
                      checked={form.experience.activityLevel === value}
                      onChange={() =>
                        handleChange("experience", "activityLevel", value)
                      }
                      onBlur={() => handleBlur("experience", "activityLevel")}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {errors["experience.activityLevel"] && (
                <p className="field-error">{errors["experience.activityLevel"]}</p>
              )}
            </fieldset>

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Primary goal <span className="required">*</span>
              </legend>
              <div className="option-group">
                {Object.entries(goalLabels).map(([value, label]) => (
                  <label key={value} className="option-pill">
                    <input
                      type="radio"
                      name="experience-goal"
                      checked={form.experience.goal === value}
                      onChange={() => handleChange("experience", "goal", value)}
                      onBlur={() => handleBlur("experience", "goal")}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {errors["experience.goal"] && (
                <p className="field-error">{errors["experience.goal"]}</p>
              )}
            </fieldset>

            {form.experience.goal === "other" && (
              <div className="form-field field-span">
                <label htmlFor="experience-goalOther" className="field-label">
                  Goal details <span className="required">*</span>
                </label>
                <input
                  id="experience-goalOther"
                  type="text"
                  value={form.experience.goalOther}
                  onChange={(e) =>
                    handleChange("experience", "goalOther", e.target.value)
                  }
                  onBlur={() => handleBlur("experience", "goalOther")}
                  aria-invalid={Boolean(errors["experience.goalOther"])}
                  aria-describedby={
                    errors["experience.goalOther"]
                      ? "experience-goalOther-error"
                      : undefined
                  }
                />
                {errors["experience.goalOther"] && (
                  <p className="field-error" id="experience-goalOther-error">
                    {errors["experience.goalOther"]}
                  </p>
                )}
              </div>
            )}

            <div className="form-field field-span">
              <label htmlFor="experience-breedReason" className="field-label">
                Why is this breed a good fit? <span className="required">*</span>
              </label>
              <textarea
                id="experience-breedReason"
                rows={3}
                value={form.experience.breedReason}
                onChange={(e) =>
                  handleChange("experience", "breedReason", e.target.value)
                }
                onBlur={() => handleBlur("experience", "breedReason")}
                aria-invalid={Boolean(errors["experience.breedReason"])}
                aria-describedby={
                  errors["experience.breedReason"]
                    ? "experience-breedReason-error"
                    : undefined
                }
              />
              {errors["experience.breedReason"] && (
                <p className="field-error" id="experience-breedReason-error">
                  {errors["experience.breedReason"]}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Preferences</h2>
            <p>Help us understand the puppy you are hoping for.</p>
          </div>
          <div className="field-grid">
            <fieldset className="form-field field-span">
              <legend className="field-label">
                Preferred sex <span className="required">*</span>
              </legend>
              <div className="option-group">
                {Object.entries(sexLabels).map(([value, label]) => (
                  <label key={value} className="option-pill">
                    <input
                      type="radio"
                      name="preferences-preferredSex"
                      checked={form.preferences.preferredSex === value}
                      onChange={() =>
                        handleChange("preferences", "preferredSex", value)
                      }
                      onBlur={() => handleBlur("preferences", "preferredSex")}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {errors["preferences.preferredSex"] && (
                <p className="field-error">{errors["preferences.preferredSex"]}</p>
              )}
            </fieldset>

            <fieldset className="form-field field-span">
              <legend className="field-label">
                Preferred temperament <span className="required">*</span>
              </legend>
              <div className="option-group">
                {Object.entries(temperamentLabels).map(([value, label]) => (
                  <label key={value} className="option-pill">
                    <input
                      type="radio"
                      name="preferences-preferredTemperament"
                      checked={form.preferences.preferredTemperament === value}
                      onChange={() =>
                        handleChange("preferences", "preferredTemperament", value)
                      }
                      onBlur={() =>
                        handleBlur("preferences", "preferredTemperament")
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
              {errors["preferences.preferredTemperament"] && (
                <p className="field-error">
                  {errors["preferences.preferredTemperament"]}
                </p>
              )}
            </fieldset>

            <div className="form-field">
              <label htmlFor="preferences-colorPreference" className="field-label">
                Color preference
              </label>
              <input
                id="preferences-colorPreference"
                type="text"
                value={form.preferences.colorPreference}
                onChange={(e) =>
                  handleChange("preferences", "colorPreference", e.target.value)
                }
                onBlur={() => handleBlur("preferences", "colorPreference")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="preferences-timeframe" className="field-label">
                Timeframe
              </label>
              <input
                id="preferences-timeframe"
                type="text"
                value={form.preferences.timeframe}
                onChange={(e) =>
                  handleChange("preferences", "timeframe", e.target.value)
                }
                onBlur={() => handleBlur("preferences", "timeframe")}
              />
            </div>

            <div className="form-field field-span">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.preferences.acknowledgesNoFirstChoice}
                  onChange={(e) =>
                    handleChange(
                      "preferences",
                      "acknowledgesNoFirstChoice",
                      e.target.checked
                    )
                  }
                  onBlur={() =>
                    handleBlur("preferences", "acknowledgesNoFirstChoice")
                  }
                />
                I understand that I may not receive my first choice puppy{" "}
                <span className="required">*</span>
              </label>
              {errors["preferences.acknowledgesNoFirstChoice"] && (
                <p className="field-error">
                  {errors["preferences.acknowledgesNoFirstChoice"]}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Training</h2>
            <p>Your training and enrichment plans.</p>
          </div>
          <div className="field-grid">
            <fieldset className="form-field field-span">
              <legend className="field-label">
                Will you attend training? <span className="required">*</span>
              </legend>
              <div className="option-group">
                <label className="option-pill">
                  <input
                    type="radio"
                    name="training-willAttendTraining"
                    checked={form.training.willAttendTraining === true}
                    onChange={() =>
                      handleChange("training", "willAttendTraining", true)
                    }
                    onBlur={() => handleBlur("training", "willAttendTraining")}
                  />
                  Yes
                </label>
                <label className="option-pill">
                  <input
                    type="radio"
                    name="training-willAttendTraining"
                    checked={form.training.willAttendTraining === false}
                    onChange={() =>
                      handleChange("training", "willAttendTraining", false)
                    }
                    onBlur={() => handleBlur("training", "willAttendTraining")}
                  />
                  No
                </label>
              </div>
              {errors["training.willAttendTraining"] && (
                <p className="field-error">{errors["training.willAttendTraining"]}</p>
              )}
            </fieldset>

            <div className="form-field field-span">
              <label htmlFor="training-trainingApproach" className="field-label">
                Training approach
              </label>
              <textarea
                id="training-trainingApproach"
                rows={3}
                value={form.training.trainingApproach}
                onChange={(e) =>
                  handleChange("training", "trainingApproach", e.target.value)
                }
                onBlur={() => handleBlur("training", "trainingApproach")}
              />
            </div>

            <div className="form-field field-span">
              <label htmlFor="training-puppyChallengesPlan" className="field-label">
                Plan for puppy challenges <span className="required">*</span>
              </label>
              <textarea
                id="training-puppyChallengesPlan"
                rows={3}
                value={form.training.puppyChallengesPlan}
                onChange={(e) =>
                  handleChange("training", "puppyChallengesPlan", e.target.value)
                }
                onBlur={() => handleBlur("training", "puppyChallengesPlan")}
                aria-invalid={Boolean(errors["training.puppyChallengesPlan"])}
                aria-describedby={
                  errors["training.puppyChallengesPlan"]
                    ? "training-puppyChallengesPlan-error"
                    : undefined
                }
              />
              {errors["training.puppyChallengesPlan"] && (
                <p
                  className="field-error"
                  id="training-puppyChallengesPlan-error"
                >
                  {errors["training.puppyChallengesPlan"]}
                </p>
              )}
            </div>

            <div className="form-field field-span">
              <label htmlFor="training-exercisePlan" className="field-label">
                Exercise plan <span className="required">*</span>
              </label>
              <textarea
                id="training-exercisePlan"
                rows={3}
                value={form.training.exercisePlan}
                onChange={(e) =>
                  handleChange("training", "exercisePlan", e.target.value)
                }
                onBlur={() => handleBlur("training", "exercisePlan")}
                aria-invalid={Boolean(errors["training.exercisePlan"])}
                aria-describedby={
                  errors["training.exercisePlan"]
                    ? "training-exercisePlan-error"
                    : undefined
                }
              />
              {errors["training.exercisePlan"] && (
                <p className="field-error" id="training-exercisePlan-error">
                  {errors["training.exercisePlan"]}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Veterinarian</h2>
            <p>Your veterinary care plan.</p>
          </div>
          <div className="field-grid">
            <fieldset className="form-field field-span">
              <legend className="field-label">
                Do you have a veterinarian? <span className="required">*</span>
              </legend>
              <div className="option-group">
                <label className="option-pill">
                  <input
                    type="radio"
                    name="vet-hasVeterinarian"
                    checked={form.vet.hasVeterinarian === true}
                    onChange={() =>
                      handleChange("vet", "hasVeterinarian", true)
                    }
                    onBlur={() => handleBlur("vet", "hasVeterinarian")}
                  />
                  Yes
                </label>
                <label className="option-pill">
                  <input
                    type="radio"
                    name="vet-hasVeterinarian"
                    checked={form.vet.hasVeterinarian === false}
                    onChange={() =>
                      handleChange("vet", "hasVeterinarian", false)
                    }
                    onBlur={() => handleBlur("vet", "hasVeterinarian")}
                  />
                  No
                </label>
              </div>
              {errors["vet.hasVeterinarian"] && (
                <p className="field-error">{errors["vet.hasVeterinarian"]}</p>
              )}
            </fieldset>

            {form.vet.hasVeterinarian && (
              <div className="form-field field-span">
                <label htmlFor="vet-vetDetails" className="field-label">
                  Veterinarian details <span className="required">*</span>
                </label>
                <textarea
                  id="vet-vetDetails"
                  rows={3}
                  value={form.vet.vetDetails}
                  onChange={(e) =>
                    handleChange("vet", "vetDetails", e.target.value)
                  }
                  onBlur={() => handleBlur("vet", "vetDetails")}
                  aria-invalid={Boolean(errors["vet.vetDetails"])}
                  aria-describedby={
                    errors["vet.vetDetails"] ? "vet-vetDetails-error" : undefined
                  }
                />
                {errors["vet.vetDetails"] && (
                  <p className="field-error" id="vet-vetDetails-error">
                    {errors["vet.vetDetails"]}
                  </p>
                )}
              </div>
            )}

            <div className="form-field field-span">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.vet.agreesRoutineCare}
                  onChange={(e) =>
                    handleChange("vet", "agreesRoutineCare", e.target.checked)
                  }
                  onBlur={() => handleBlur("vet", "agreesRoutineCare")}
                />
                I agree to routine veterinary care{" "}
                <span className="required">*</span>
              </label>
              {errors["vet.agreesRoutineCare"] && (
                <p className="field-error">{errors["vet.agreesRoutineCare"]}</p>
              )}
            </div>

            <div className="form-field field-span">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.vet.understandsGrooming}
                  onChange={(e) =>
                    handleChange("vet", "understandsGrooming", e.target.checked)
                  }
                  onBlur={() => handleBlur("vet", "understandsGrooming")}
                />
                I understand grooming requirements{" "}
                <span className="required">*</span>
              </label>
              {errors["vet.understandsGrooming"] && (
                <p className="field-error">{errors["vet.understandsGrooming"]}</p>
              )}
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Commitment</h2>
            <p>Your long-term plan for this puppy.</p>
          </div>
          <div className="field-grid">
            <fieldset className="form-field field-span">
              <legend className="field-label">
                Are you financially prepared? <span className="required">*</span>
              </legend>
              <div className="option-group">
                <label className="option-pill">
                  <input
                    type="radio"
                    name="commitment-financiallyPrepared"
                    checked={form.commitment.financiallyPrepared === true}
                    onChange={() =>
                      handleChange("commitment", "financiallyPrepared", true)
                    }
                    onBlur={() =>
                      handleBlur("commitment", "financiallyPrepared")
                    }
                  />
                  Yes
                </label>
                <label className="option-pill">
                  <input
                    type="radio"
                    name="commitment-financiallyPrepared"
                    checked={form.commitment.financiallyPrepared === false}
                    onChange={() =>
                      handleChange("commitment", "financiallyPrepared", false)
                    }
                    onBlur={() =>
                      handleBlur("commitment", "financiallyPrepared")
                    }
                  />
                  No
                </label>
              </div>
              {errors["commitment.financiallyPrepared"] && (
                <p className="field-error">
                  {errors["commitment.financiallyPrepared"]}
                </p>
              )}
            </fieldset>

            <div className="form-field field-span">
              <label htmlFor="commitment-rehomeReasons" className="field-label">
                If you could not keep the dog, what is your plan?{" "}
                <span className="required">*</span>
              </label>
              <textarea
                id="commitment-rehomeReasons"
                rows={3}
                value={form.commitment.rehomeReasons}
                onChange={(e) =>
                  handleChange("commitment", "rehomeReasons", e.target.value)
                }
                onBlur={() => handleBlur("commitment", "rehomeReasons")}
                aria-invalid={Boolean(errors["commitment.rehomeReasons"])}
                aria-describedby={
                  errors["commitment.rehomeReasons"]
                    ? "commitment-rehomeReasons-error"
                    : undefined
                }
              />
              {errors["commitment.rehomeReasons"] && (
                <p className="field-error" id="commitment-rehomeReasons-error">
                  {errors["commitment.rehomeReasons"]}
                </p>
              )}
            </div>

            <div className="form-field field-span">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.commitment.agreesContactBreederFirst}
                  onChange={(e) =>
                    handleChange(
                      "commitment",
                      "agreesContactBreederFirst",
                      e.target.checked
                    )
                  }
                  onBlur={() =>
                    handleBlur("commitment", "agreesContactBreederFirst")
                  }
                />
                I agree to contact the breeder first if rehoming is needed{" "}
                <span className="required">*</span>
              </label>
              {errors["commitment.agreesContactBreederFirst"] && (
                <p className="field-error">
                  {errors["commitment.agreesContactBreederFirst"]}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Signature</h2>
            <p>Agreement and confirmation.</p>
          </div>
          <div className="field-grid">
            <div className="form-field field-span">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.signature.certifyTruth}
                  onChange={(e) =>
                    handleChange("signature", "certifyTruth", e.target.checked)
                  }
                  onBlur={() => handleBlur("signature", "certifyTruth")}
                />
                I certify all information is true{" "}
                <span className="required">*</span>
              </label>
              {errors["signature.certifyTruth"] && (
                <p className="field-error">{errors["signature.certifyTruth"]}</p>
              )}
            </div>

            <div className="form-field field-span">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.signature.understandsNoGuarantee}
                  onChange={(e) =>
                    handleChange(
                      "signature",
                      "understandsNoGuarantee",
                      e.target.checked
                    )
                  }
                  onBlur={() =>
                    handleBlur("signature", "understandsNoGuarantee")
                  }
                />
                I understand placement is not guaranteed{" "}
                <span className="required">*</span>
              </label>
              {errors["signature.understandsNoGuarantee"] && (
                <p className="field-error">
                  {errors["signature.understandsNoGuarantee"]}
                </p>
              )}
            </div>

            <div className="form-field field-span">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.signature.breederMayDecline}
                  onChange={(e) =>
                    handleChange(
                      "signature",
                      "breederMayDecline",
                      e.target.checked
                    )
                  }
                  onBlur={() => handleBlur("signature", "breederMayDecline")}
                />
                I acknowledge the breeder may decline my application{" "}
                <span className="required">*</span>
              </label>
              {errors["signature.breederMayDecline"] && (
                <p className="field-error">
                  {errors["signature.breederMayDecline"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="signature-name" className="field-label">
                Signature name <span className="required">*</span>
              </label>
              <input
                id="signature-name"
                type="text"
                value={form.signature.signatureName}
                onChange={(e) =>
                  handleChange("signature", "signatureName", e.target.value)
                }
                onBlur={() => handleBlur("signature", "signatureName")}
                aria-invalid={Boolean(errors["signature.signatureName"])}
                aria-describedby={
                  errors["signature.signatureName"]
                    ? "signature-name-error"
                    : undefined
                }
              />
              {errors["signature.signatureName"] && (
                <p className="field-error" id="signature-name-error">
                  {errors["signature.signatureName"]}
                </p>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="signature-date" className="field-label">
                Signature date <span className="required">*</span>
              </label>
              <input
                id="signature-date"
                type="date"
                value={form.signature.signatureDate}
                onChange={(e) =>
                  handleChange("signature", "signatureDate", e.target.value)
                }
                onBlur={() => handleBlur("signature", "signatureDate")}
                aria-invalid={Boolean(errors["signature.signatureDate"])}
                aria-describedby={
                  errors["signature.signatureDate"]
                    ? "signature-date-error"
                    : undefined
                }
              />
              {errors["signature.signatureDate"] && (
                <p className="field-error" id="signature-date-error">
                  {errors["signature.signatureDate"]}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="puppy-section">
          <div className="section-header">
            <h2>Next step</h2>
            <p>Review your entries on the next page before submitting.</p>
          </div>
          <div className="review-actions">
            <button type="submit" className="primary-btn">
              Review Application
            </button>
            <button type="button" className="ghost-btn" onClick={handleReset}>
              Reset Form
            </button>
          </div>
          {hasErrors ? (
            <p className="field-error review-inline-error">
              Please fix the highlighted fields before reviewing.
            </p>
          ) : (
            <p className="review-note">
              You will be able to submit the application after reviewing.
            </p>
          )}
        </section>
      </form>
    </div>
  );
}
